import React, { useState } from 'react';

import {
  TextField,
  Button,
  Typography,
  Box,
  CircularProgress,
} from '@mui/material';
import Autocomplete from '@mui/material/Autocomplete';
import useAxiosPrivate from '../../../../hooks/useAxiosPrivate';
import { User, Document } from '../../../../types/workspace';
import { ModalType } from '../../../../types/modal';

interface CreateWorkspaceFormProps {
  users: User[];
  documents: Document[];
  loading: boolean;
  setShowModal: React.Dispatch<React.SetStateAction<boolean>>;
  setModalInfo: React.Dispatch<React.SetStateAction<ModalType>>;
}

const CreateWorkspaceForm = ({
  users,
  documents,
  loading,
  setShowModal,
  setModalInfo,
}: CreateWorkspaceFormProps) => {
  const axiosPrivate = useAxiosPrivate();

  const [workspaceName, setWorkspaceName] = useState('');
  const [selectedUsers, setSelectedUsers] = useState<User[]>([]);
  const [selectedDocuments, setSelectedDocuments] = useState<Document[]>([]);

  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    setIsSubmitting(true);
    e.preventDefault();
    try {
      await axiosPrivate.post('/admin/workspace/create-workspace', {
        name: workspaceName,
        users: selectedUsers,
        documents: selectedDocuments,
      });
      // Reset form
      setWorkspaceName('');
      setSelectedUsers([]);
      setSelectedDocuments([]);
      setModalInfo({
        titleText: 'Success!',
        bodyText: `Workspace:  ${workspaceName} created successfully!`,
        autoClose: true,
      });
      setShowModal(true);
    } catch (error) {
      console.error('Error creating workspace:', error);
    } finally {
      setIsSubmitting(false);
    }
  };
  if (loading) {
    return <CircularProgress size={48} />;
  }

  return (
    <Box component="form" onSubmit={handleSubmit} sx={{ mt: 3 }}>
      <Typography variant="h4" align="center" component="h1" gutterBottom>
        Create Workspace
      </Typography>
      <Box sx={{ mb: 2 }}>
        <TextField
          label="Workspace Name"
          variant="outlined"
          fullWidth
          value={workspaceName}
          onChange={(e) => setWorkspaceName(e.target.value)}
          required
        />
      </Box>
      <Box sx={{ mb: 2 }}>
        <Autocomplete
          multiple
          options={users}
          getOptionLabel={(user) => `${user.firstName} ${user.lastName}`}
          value={selectedUsers}
          onChange={(event, newValue) => setSelectedUsers(newValue)}
          renderInput={(params) => (
            <TextField {...params} variant="outlined" label="Select Users" />
          )}
        />
      </Box>
      <Box sx={{ mb: 2 }}>
        <Autocomplete
          multiple
          options={documents}
          getOptionLabel={(doc) => doc.fileName}
          value={selectedDocuments}
          onChange={(event, newValue) => setSelectedDocuments(newValue)}
          renderInput={(params) => (
            <TextField
              {...params}
              variant="outlined"
              label="Select Documents"
            />
          )}
        />
      </Box>

      <Box sx={{ display: 'flex', justifyContent: 'end', marginTop: 2 }}>
        <Box>
          <Button
            disabled={isSubmitting}
            type="submit"
            variant="contained"
            color="primary"
            sx={{ marginLeft: 2 }}
          >
            Create Workspace
          </Button>
          {isSubmitting && <CircularProgress size={24} />}
        </Box>
      </Box>
    </Box>
  );
};

export default CreateWorkspaceForm;
